/**
 * Which PROPERTY of a Book or Draft earned it its place in a proposed Project,
 * and how strongly.
 *
 * ## Why this exists
 *
 * `joinReason` already says why one member is in a cluster, and
 * `ProjectMappingReasonComponent` renders it as a sentence. A sentence per
 * item is correct but it does not *compare*: given six items the reader has to
 * hold six sentences in their head to answer "so what actually ties this
 * group together?".
 *
 * So the review surface also draws every member as a card with the SAME facet
 * rows in the SAME order, and colours each row by what that facet contributed
 * for that member. Title sits where title always sits, interior PDF where
 * interior PDF always sits. The eye compares down a column instead of reading
 * prose, and a group held together by its interior PDF looks different at a
 * glance from one held together by a fuzzy title match.
 *
 * ## This table is a restatement of the contract, not a judgement
 *
 * Every row below is taken from `ProjectMappingReview.md` section 6, which
 * defines the reason codes and their order. Nothing here infers anything the
 * server did not say:
 *
 * - `BODY_RESOURCE_EXACT` is defined as "same interior PDF, same page count,
 *   same trim", so it marks three facets, not one.
 * - `BODY_RESOURCE_ONLY` is defined as "same interior PDF, **titles differ**".
 *   That makes the title a known MISMATCH, which is information worth drawing
 *   — it is the single most likely reason a reader will disagree with a
 *   grouping — and is why `DIFFERS` exists as a distinct strength rather than
 *   being flattened into `NEUTRAL`.
 * - `COVER_RESOURCE` is "corroboration only, never forms a group". It gets its
 *   own strength so it can never be drawn as though it were decisive.
 *
 * ## No confidence number, here or anywhere
 *
 * The contract is explicit that no confidence figure crosses the wire, so that
 * a customer never reads "71%" off a screen and argues with it. These
 * strengths are ordinal labels derived from the code alone. Do not add a
 * numeric score to this file, and do not render one.
 *
 * No Angular import: this is a pure table so the smoke test can load it under
 * plain ts-node, the same arrangement as `project-partition.ts`.
 */

import {ProjectMappingJoinReason} from '../../../model/ProjectMapping';
import {MappingComparison} from './mapping-compare';

/**
 * The facet rows, in the order they are drawn.
 *
 * **The order is the feature.** Fixed position is what lets the reader scan a
 * column across several cards; re-ordering per item, or hiding a facet that
 * did not contribute, would destroy exactly the comparison this is for. A
 * facet that contributed nothing is drawn `NEUTRAL`, never omitted.
 */
export type MappingFacetKey =
  | 'TITLE'
  | 'AUTHOR'
  | 'BODY'
  | 'COVER'
  | 'PAGES'
  | 'TRIM'
  | 'BINDING'
  | 'PAPER'
  | 'LAMINATION'
  | 'IDENTIFIER'
  | 'PROJECT'
  | 'UPDATED';

/**
 * `UPDATED` is last, and it is the one row that never contributes to a match.
 *
 * No `joinReason` implicates a date, so it is always `NEUTRAL` - but it is not
 * decoration. Two of the three `decisionReasons` are about recency rather than
 * identity (`PRIMARY_AMBIGUOUS`, `PRIMARY_STALE`), and `primaryRule: RECENCY`
 * literally means "most recently edited". Before this row existed the screen
 * asserted those things without ever showing the dates they rest on, so a
 * reader asked to confirm the current version had no way to check the claim.
 *
 * It sits after the identity facets deliberately: everything above answers
 * "are these the same work?", this answers "which one is current?".
 */
export const MAPPING_FACET_ORDER: MappingFacetKey[] = [
  'TITLE', 'AUTHOR', 'BODY', 'COVER',
  // The physical specification, kept together: these are what make two
  // same-titled items different PRODUCTS rather than different files.
  'PAGES', 'TRIM', 'BINDING', 'PAPER', 'LAMINATION',
  'IDENTIFIER', 'PROJECT', 'UPDATED'
];

/**
 * How much a facet contributed, strongest first.
 *
 * - `ANCHOR`       the seed — the item the cluster formed around, which joined
 *                  on nothing because everything else joined to IT.
 * - `DECISIVE`     this facet is why the member is here.
 * - `SUPPORTING`   corroborated the decisive facet and is part of the named rule.
 * - `CORROBORATING`agrees, but can never form a group on its own (cover PDF).
 * - `WEAK`         matched only approximately, or only on a normalised core.
 * - `DIFFERS`      known NOT to match, and the grouping was made anyway.
 * - `NEUTRAL`      not part of this rule; shown for position, says nothing.
 */
export type MappingFacetStrength =
  | 'ANCHOR'
  | 'DECISIVE'
  | 'SUPPORTING'
  | 'CORROBORATING'
  | 'WEAK'
  | 'DIFFERS'
  | 'NEUTRAL';

export type MappingFacetMap = { [facet: string]: MappingFacetStrength };

/**
 * Reason code -> which facets it implicates. Section 6, line by line.
 *
 * A code absent from this map yields all-`NEUTRAL`, which is the right failure
 * mode when the server adds a code before the client ships a table row for it:
 * the card still draws, in the right shape, claiming nothing.
 */
const FACETS_BY_REASON: { [reason: string]: MappingFacetMap } = {
  // Joined on nothing; see ANCHOR above. Handled specially by `facetsFor`.
  SEED: {},

  /**
   * Everything the planner compares agrees, so every one of those facets is
   * marked - which is the whole point of the code. The reader sees a solid
   * column rather than three green rows in a field of grey, and that shape is
   * exactly what "these are the same thing" should look like.
   *
   * `PROJECT` and `UPDATED` are excluded: neither is compared to produce this
   * verdict, and `UPDATED` is never evidence of a match at all.
   */
  IDENTICAL: {
    TITLE: 'DECISIVE', AUTHOR: 'DECISIVE', BODY: 'DECISIVE', COVER: 'DECISIVE',
    PAGES: 'DECISIVE', TRIM: 'DECISIVE', IDENTIFIER: 'DECISIVE'
  },

  WORK_ID_EXISTING:    { PROJECT: 'DECISIVE' },
  ISBN_EXACT:          { IDENTIFIER: 'DECISIVE' },
  SFBN_EXACT:          { IDENTIFIER: 'DECISIVE' },

  BODY_RESOURCE_EXACT: { BODY: 'DECISIVE', PAGES: 'SUPPORTING', TRIM: 'SUPPORTING' },
  BODY_RESOURCE_TITLE: { BODY: 'DECISIVE', TITLE: 'SUPPORTING' },
  BODY_RESOURCE_ONLY:  { BODY: 'DECISIVE', TITLE: 'DIFFERS' },

  TITLE_AUTHOR_PAGES:  { TITLE: 'DECISIVE', AUTHOR: 'SUPPORTING', PAGES: 'SUPPORTING' },
  TITLE_AUTHOR:        { TITLE: 'DECISIVE', AUTHOR: 'SUPPORTING' },
  TITLE_EXACT:         { TITLE: 'DECISIVE' },
  TITLE_CORE:          { TITLE: 'WEAK' },
  TITLE_FUZZY:         { TITLE: 'WEAK' },

  COVER_RESOURCE:      { COVER: 'CORROBORATING' },
};

/**
 * The strength of every facet for one member, with no gaps.
 *
 * Always returns a value for every key in {@link MAPPING_FACET_ORDER}, so the
 * template can iterate the order directly and never has to test for absence.
 */
export function facetsFor(reason: ProjectMappingJoinReason): MappingFacetMap {
  const result: MappingFacetMap = {};
  const isSeed = reason === 'SEED';
  const defined = FACETS_BY_REASON[reason] || {};
  for ( const key of MAPPING_FACET_ORDER ) {
    result[key] = isSeed ? 'ANCHOR' : (defined[key] || 'NEUTRAL');
  }
  return result;
}

/**
 * One drawn row: which facet, how strongly it contributed, and the item's
 * actual value for it.
 *
 * `value` starts empty and is filled in once the Book or Draft resolves. An
 * empty value still draws its row - the position is the point, and a missing
 * row would shift everything below it out of alignment with the neighbouring
 * cards.
 */
/**
 * How one item's date relates to the most recent item in its group.
 *
 * The absolute date alone does not answer the question a reader is holding -
 * "is this the current one?" is relative, and subtracting two dates in your
 * head across six cards is exactly the work the grid exists to remove.
 */
export type MappingRecencyNote = 'MOST_RECENT' | 'DAYS_OLDER' | 'DAYS_NEWER';

/** Which way an ordinal value moved against the card before it. */
export type MappingFacetDirection = 'NEWER' | 'OLDER';

export interface MappingFacetRow {
  key: MappingFacetKey;
  strength: MappingFacetStrength;
  value: string;
  /**
   * The same value as a raw number, where one exists.
   *
   * `value` is localized for display - Arabic-Indic digits in `ar` - so it
   * cannot be compared arithmetically. This is what ordinal comparison reads.
   */
  numericValue?: number;
  /**
   * For resource ids: is this one NEWER or OLDER than the card before it?
   *
   * Resource ids are issued in ascending order, so "318516 vs 318512" is not
   * merely a difference, it says which artwork came second. That is the
   * question a reader actually has when two versions disagree on a PDF, and
   * "differs" leaves them to work it out from the digits.
   */
  direction?: MappingFacetDirection;
  /**
   * Epoch milliseconds, for the `UPDATED` row only.
   *
   * Kept as a number rather than a pre-formatted string so the template can
   * use Angular's `date` pipe, which formats to the build's `LOCALE_ID`.
   * Formatting it in TypeScript would hard-code the browser's locale instead
   * and quietly disagree with every other date in the app.
   */
  valueDate?: number;
  /** Localized day count accompanying {@link detailKind}. */
  detail?: string;
  /** Which recency sentence to render. Absent on every other row. */
  detailKind?: MappingRecencyNote;
  /**
   * How this value compares to the SAME row on the card to its left.
   *
   * A second, independent axis from {@link strength}: strength says what the
   * row contributed to the match, this says whether two versions agree. See
   * `mapping-compare.ts`. Absent on the first card and on any row where
   * either value is unknown.
   */
  comparison?: MappingComparison;
}

/** Every row for one reason, in {@link MAPPING_FACET_ORDER}, values empty. */
export function buildFacetRows(reason: ProjectMappingJoinReason): MappingFacetRow[] {
  const strengths = facetsFor(reason);
  return MAPPING_FACET_ORDER.map(key => ({ key: key, strength: strengths[key], value: '' }));
}

/**
 * Facets never drawn, whatever the group looks like.
 *
 * `AUTHOR` earns its place nowhere: on a screen comparing versions of ONE
 * work the author is the same on every card by construction, so the row is a
 * column of identical text that costs a line on every card and resolves
 * nothing. It stays in the table and in `MappingItemFacts` - it is still
 * fetched, and restoring the row is one entry here - but it is not drawn.
 */
const NEVER_DRAWN: MappingFacetKey[] = ['AUTHOR'];

/**
 * Facets drawn only when they are what formed the group.
 *
 * An ISBN is noise on a group matched by its interior PDF - a column of blanks
 * on Drafts, which carry no identifier at all, and an irrelevant number on the
 * Books. It is decisive information on a group matched BY the identifier, and
 * that is the only case it appears in.
 *
 * `IDENTICAL` deliberately does not qualify. It means every facet agreed,
 * including the identifier, but the identifier is not what *bound* the group -
 * and since `IDENTICAL` is the commonest code in the estate, treating it as a
 * trigger would show the row almost everywhere and undo the point.
 */
const DRAWN_ONLY_IF_BINDING: { [facet: string]: ProjectMappingJoinReason[] } = {
  IDENTIFIER: ['ISBN_EXACT', 'SFBN_EXACT'],
};

/**
 * The facet rows to draw for one proposal, in order.
 *
 * **Decided per PROPOSAL, never per card.** Every card in a group must draw
 * the same rows in the same positions or the cross-card comparison the grid
 * exists for is destroyed - so this takes every reason in the group at once
 * and returns one answer for all of them.
 */
export function visibleFacetKeys(reasons: ProjectMappingJoinReason[]): MappingFacetKey[] {
  const present = reasons || [];
  return MAPPING_FACET_ORDER.filter(key => {
    if ( NEVER_DRAWN.indexOf(key) >= 0 ) {
      return false;
    }
    const triggers = DRAWN_ONLY_IF_BINDING[key];
    if ( !triggers ) {
      return true;
    }
    return present.some(reason => triggers.indexOf(reason) >= 0);
  });
}

/** The facets this reason actually says something about, in draw order. */
export function contributingFacets(reason: ProjectMappingJoinReason): MappingFacetKey[] {
  const map = FACETS_BY_REASON[reason] || {};
  return MAPPING_FACET_ORDER.filter(key => !!map[key]);
}

/**
 * Why a near miss was NOT included.
 *
 * ## Read this before extending it
 *
 * **The server does not send a rejection reason.** `ProjectMappingNearMiss` is
 * `{ itemType, item_ID, reason }` and `reason` is the code on which the item
 * *connected*, not the one on which it was dropped. An explicit field has been
 * requested (outbox 2026-10-26); until it lands, this derives only what the
 * contract itself states and refuses to guess beyond that:
 *
 * - `CORROBORATION_ONLY` — section 6 says in as many words that
 *   `COVER_RESOURCE` "never forms a group". That is a fact about the rule, not
 *   an inference about this item, so it is safe to show as the reason.
 * - `TITLE_ONLY_WEAK` — `TITLE_CORE` and `TITLE_FUZZY` are the two weakest
 *   codes in the section 6 ordering and both are title-only. Saying "title
 *   similarity alone was not enough" restates the ordering.
 * - `BELOW_THRESHOLD` — everything else. Deliberately vague, because anything
 *   more specific would be invented. A near miss on a strong code was dropped
 *   for a reason this client genuinely cannot see.
 *
 * Do not add confident-sounding wording to the `BELOW_THRESHOLD` branch. The
 * honest answer is the vague one until the server sends the real code.
 */
export type MappingNearMissRejection =
  | 'CORROBORATION_ONLY'
  | 'TITLE_ONLY_WEAK'
  | 'BELOW_THRESHOLD';

export function nearMissRejection(reason: ProjectMappingJoinReason): MappingNearMissRejection {
  if ( reason === 'COVER_RESOURCE' ) {
    return 'CORROBORATION_ONLY';
  }
  if ( reason === 'TITLE_CORE' || reason === 'TITLE_FUZZY' ) {
    return 'TITLE_ONLY_WEAK';
  }
  return 'BELOW_THRESHOLD';
}
